// src/components/founderDashboard/UsageDashboard.tsx
// Founder usage analytics: workout completions, active trainers, and REAL
// DAU/WAU/MAU from the DailyActive table (recording starts at its deploy -
// earlier daily activity genuinely does not exist and is not fabricated).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area, LabelList,
    XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api } from "./apiClient";
import EngagementCard from "./EngagementCard";
import {
    C, FONT, numStyle, DISC, discColor, parseDate, dayKey, fmt, shortDate, monthFormatter,
    RangePicker, RangeSel, LIVE_DATE, Shell, KpiStrip, Card, Grid, Legend, StatBar, BarRow,
    Empty, axisX, axisY, gridProps, tipBar, tipLine, barRadius, TipBox, th, td, tdNum, TableWrap,
} from "./hq";

type Row = Record<string, any>;

const TYPES = ["Gym", "Running", "Hiit", "Mobility", "Hyrox", "Other"] as const;
const TYPE_LABEL: Record<string, string> = { Hiit: "HIIT" };
const bucket = (t: string) => (TYPES as readonly string[]).includes(t) ? t : "Other";
const PROGRAMME_PREVIEW = 20;

// Floating tooltip for the stacked weekly chart: each type, then the week's
// total, with empty types dropped so quiet weeks stay compact.
function StackTotalTip({ active, payload, label }: any) {
    if (!active || !payload || !payload.length) return null;
    const rows = payload.filter((p: any) => Number(p.value) > 0);
    const total = payload.reduce((s: number, p: any) => s + (Number(p.value) || 0), 0);
    return (
        <TipBox title={`w/c ${shortDate(label)}`}>
            {rows.map((p: any) => (
                <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 6, padding: "1px 0" }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
                    {TYPE_LABEL[p.dataKey] || p.dataKey}: {fmt(p.value)}
                </div>
            ))}
            <div style={{ fontWeight: 700, marginTop: 5, paddingTop: 5, borderTop: `1px solid ${C.hair}` }}>
                Total: {fmt(total)}
            </div>
        </TipBox>
    );
}

export default function UsageDashboard() {
    const [data, setData] = useState<Row | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [range, setRange] = useState<RangeSel>({ kind: "preset", days: 180 });
    const [showAll, setShowAll] = useState(false);

    const now = useMemo(() => new Date(), []);
    const { winStart, winEnd } = useMemo(() => {
        if (range.kind === "all") return { winStart: null as Date | null, winEnd: null as Date | null };
        if (range.kind === "since") return { winStart: parseDate(LIVE_DATE), winEnd: null as Date | null };
        if (range.kind === "preset") {
            const d = new Date(now); d.setUTCDate(d.getUTCDate() - range.days);
            return { winStart: d, winEnd: null as Date | null };
        }
        return { winStart: parseDate(range.start), winEnd: parseDate(range.end) };
    }, [range, now]);
    const inWindow = useCallback(
        (d: Date | null) => !!d && (!winStart || d >= winStart) && (!winEnd || d <= winEnd),
        [winStart, winEnd]);

    const [schedule, setSchedule] = useState<Row[]>([]);
    useEffect(() => {
        api.get("/founder-portal/week-schedule/")
            .then(res => {
                const d = res.data;
                setSchedule(Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : []));
            })
            .catch(() => setSchedule([]));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (winStart) params.set("start", winStart.toISOString().slice(0, 10));
        if (winEnd) params.set("end", winEnd.toISOString().slice(0, 10));
        api.get(`/founder-portal/usage-metrics/?${params.toString()}`)
            .then(res => setData(res.data))
            .catch(e => setErr(e?.response?.data?.error || e?.message || "Failed to load"));
    }, [winStart, winEnd]);

    // weekly completions, stacked by type
    const weeklyStacked = useMemo(() => {
        const weeks = new Map<string, Row>();
        (data?.weekly_types || []).forEach((r: Row) => {
            const d = parseDate(r.week);
            if (!inWindow(d)) return;
            const w = weeks.get(r.week) || { week: r.week };
            const b = bucket(String(r.type));
            w[b] = (w[b] || 0) + r.n;
            weeks.set(r.week, w);
        });
        // watch-pushed rides on top as its own segment - the type rows above
        // are Burst-only server-side, so nothing is double-counted
        (data?.weekly || []).forEach((r: Row) => {
            if (!(r.watch > 0) || !inWindow(parseDate(r.week))) return;
            const w = weeks.get(r.week) || { week: r.week };
            w["Watch-pushed"] = r.watch;
            weeks.set(r.week, w);
        });
        return Array.from(weeks.values()).sort((a, b) => String(a.week).localeCompare(String(b.week)));
    }, [data, inWindow]);

    const weekly = useMemo(() => (data?.weekly || [])
        .filter((r: Row) => inWindow(parseDate(r.week)))
        .map((r: Row) => ({
            ...r,
            burst_trainers: r.burst_trainers ?? r.trainers,
            watch_only: Math.max(0, (r.trainers || 0) - (r.burst_trainers ?? r.trainers)),
        })), [data, inWindow]);

    const cumulative = useMemo(() => {
        let acc = 0;
        return (data?.weekly || []).map((r: Row) => {
            acc += r.completions;
            return { week: r.week, cumulative: acc, d: parseDate(r.week) };
        }).filter((r: Row) => inWindow(r.d));
    }, [data, inWindow]);

    const monthly = useMemo(() => (data?.monthly || [])
        .filter((r: Row) => inWindow(parseDate(r.month)))
        .map((r: Row) => {
            const burstTrainers = r.burst_trainers ?? r.trainers;
            return {
                ...r,
                label: String(r.month).slice(0, 7),
                burst: (r.completions || 0) - (r.watch || 0),
                watch: r.watch || 0,
                burst_trainers: burstTrainers,
                watch_only: Math.max(0, (r.trainers || 0) - burstTrainers),
            };
        }), [data, inWindow]);
    const monthTick = useMemo(() => monthFormatter(monthly.map((m: Row) => m.label)), [monthly]);

    const regsWeekly = useMemo(() => (data?.registrations_weekly || [])
        .filter((r: Row) => inWindow(parseDate(r.week))), [data, inWindow]);
    const regPeak = useMemo(() => Math.max(0, ...regsWeekly.map((r: Row) => r.n || 0)), [regsWeekly]);

    const mix = useMemo(() => {
        const totals = new Map<string, number>();
        (data?.weekly_types || []).forEach((r: Row) => {
            if (!inWindow(parseDate(r.week))) return;
            const b = bucket(String(r.type));
            totals.set(b, (totals.get(b) || 0) + r.n);
        });
        const total = Array.from(totals.values()).reduce((a, b) => a + b, 0);
        return {
            total,
            rows: Array.from(totals.entries())
                .map(([name, n]) => ({ name, n }))
                .sort((a, b) => b.n - a.n),
        };
    }, [data, inWindow]);

    // everything scheduled this Monday-Sunday week, grouped:
    // completed first, then missed (past, not done), then upcoming
    const weekRows = useMemo(() => {
        const todayKey = dayKey(new Date());
        return (Array.isArray(schedule) ? schedule : [])
            .map((r: Row) => {
                const d = parseDate(r.scheduled_date);
                const state = r.completed ? "completed" : (d && dayKey(d) < todayKey ? "missed" : "upcoming");
                return { ...r, d, state };
            })
            .filter((r: Row) => r.d)
            .sort((a: Row, b: Row) => {
                const order: Record<string, number> = { completed: 0, missed: 1, upcoming: 2 };
                return order[a.state] - order[b.state] || a.d.getTime() - b.d.getTime() || (a.id - b.id);
            });
    }, [schedule]);
    const weekCounts = useMemo(() => ({
        done: weekRows.filter((r: Row) => r.state === "completed").length,
        missed: weekRows.filter((r: Row) => r.state === "missed").length,
        upcoming: weekRows.filter((r: Row) => r.state === "upcoming").length,
    }), [weekRows]);
    const lastActive = (iso: string | null) => {
        const d = iso ? parseDate(iso) : null;
        if (!d) return { label: "no record", color: C.faint };
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        const label = days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
        return { label, color: days <= 1 ? C.sageD : days >= 9 ? C.alert : C.ink };
    };

    const dau = useMemo(() => (data?.dau || [])
        .filter((r: Row) => inWindow(parseDate(r.date))), [data, inWindow]);
    const stickRows = useMemo(() => dau.filter((r: Row) => r.stickiness !== null && r.stickiness !== undefined), [dau]);
    const stickLatest = stickRows[stickRows.length - 1];
    const stickTop = Math.ceil((Math.max(0, ...stickRows.map((r: Row) => r.stickiness)) + 2) / 5) * 5;
    const stickTicks = Array.from({ length: stickTop / 5 + 1 }, (_, i) => i * 5);

    const picker = <RangePicker range={range} onChange={setRange} presets={[30, 90, 180]} />;

    if (err) return <Shell title="Usage" right={picker}><Empty>{err}</Empty></Shell>;
    if (!data) return <Shell title="Usage" right={picker}><div style={{ color: C.muted, fontFamily: FONT }}>Loading usage data…</div></Shell>;

    const t = data.tiles || {};
    // yesterday has no data on the first recording day - show today's count instead
    const dauShown = t.dau_yesterday > 0 ? t.dau_yesterday : (t.dau_today || 0);
    const dauHint = t.dau_yesterday > 0 ? "yesterday" : "today so far";
    const stickiness = t.mau ? (dauShown / t.mau) * 100 : null;
    // Lifetime, not window-scoped: retention answers "of everyone who ever
    // registered", so the range picker must not silently narrow it.
    const ret = data.retention as Row | null;
    const pct = (n: number, base: number) => (base ? (n / base) * 100 : 0);
    const shownRows = showAll ? weekRows : weekRows.slice(0, PROGRAMME_PREVIEW);
    const legendTypes = TYPES.map(ty => ({ label: TYPE_LABEL[ty] || ty, color: DISC[ty] }));

    return (
        <Shell title="Usage" right={picker}>
            <KpiStrip items={[
                { label: "MAU", value: t.tracking_since ? fmt(t.mau) : "-", sub: t.tracking_since ? "always last 30d" : "recording starts on deploy" },
                { label: "WAU", value: t.tracking_since ? fmt(t.wau) : "-", sub: "always last 7d" },
                { label: "DAU", value: t.tracking_since ? fmt(dauShown) : "-", sub: dauHint },
                { label: "Stickiness", value: stickiness === null ? "-" : `${fmt(stickiness)}%`, sub: "DAU / MAU" },
                { label: "Trainers", value: fmt(t.active_trainers_window), sub: "in window" },
                { label: "Completions", value: fmt(t.completions_window), sub: "in window" },
                { label: "Registrations", value: fmt(t.registrations_window), sub: "in window" },
                { label: "Members", value: fmt(t.total_members), sub: `${fmt(t.total_completions)} completions all-time` },
            ]} />

            <Grid min={300}>
                {ret && (
                    <Card title="What happens after someone registers" gap={18}>
                        <StatBar label="Opened the app again on a later day" value={`${fmt(pct(ret.returned, ret.cohort))}%`}
                            count={`${fmt(ret.returned)} of ${fmt(ret.cohort)}`} pct={pct(ret.returned, ret.cohort)} />
                        <StatBar label="Came back within their first 7 days" value={`${fmt(pct(ret.returned_7d, ret.cohort_7d))}%`}
                            count={`${fmt(ret.returned_7d)} of ${fmt(ret.cohort_7d)}`} pct={pct(ret.returned_7d, ret.cohort_7d)}
                            note="Only members who have had a full 7 days" />
                        <StatBar label="Ever completed a workout" value={`${fmt(pct(ret.cohort_trained, ret.cohort))}%`}
                            count={`${fmt(ret.cohort_trained)} of ${fmt(ret.cohort)}`} pct={pct(ret.cohort_trained, ret.cohort)} />
                        <StatBar label="Ever completed a workout, whole membership" value={`${fmt(pct(ret.total_trained, ret.total_members))}%`}
                            count={`${fmt(ret.total_trained)} of ${fmt(ret.total_members)}`} pct={pct(ret.total_trained, ret.total_members)}
                            note="Completions are recorded for everyone, all the way back" />
                    </Card>
                )}
                {ret && (
                    <Card title="How often members open the app" gap={18}>
                        <StatBar label="Most days" value={`${fmt(pct(ret.freq_daily, ret.freq_base))}%`}
                            count={`${fmt(ret.freq_daily)} of ${fmt(ret.freq_base)}`} pct={pct(ret.freq_daily, ret.freq_base)}
                            color={C.sageD} note="Opened on 70% or more of their tracked days" />
                        <StatBar label="Most weeks" value={`${fmt(pct(ret.freq_weekly, ret.freq_base))}%`}
                            count={`${fmt(ret.freq_weekly)} of ${fmt(ret.freq_base)}`} pct={pct(ret.freq_weekly, ret.freq_base)}
                            note="Present in 70%+ of tracked weeks, but not most days" />
                        <StatBar label="At least once in the last 30 days" value={`${fmt(pct(ret.freq_monthly, ret.freq_base))}%`}
                            count={`${fmt(ret.freq_monthly)} of ${fmt(ret.freq_base)}`} pct={pct(ret.freq_monthly, ret.freq_base)}
                            color={C.sageL} />
                        <StatBar label="Not opened at all since recording began" value={`${fmt(pct(ret.freq_dormant, ret.freq_base))}%`}
                            count={`${fmt(ret.freq_dormant)} of ${fmt(ret.freq_base)}`} pct={pct(ret.freq_dormant, ret.freq_base)}
                            color={C.alert} />
                    </Card>
                )}
                <Card title="Daily active users"
                    right={dau.length ? <span style={{ fontSize: 12, color: C.muted }}>{shortDate(dau[0].date)} – {shortDate(dau[dau.length - 1].date)}</span> : undefined}>
                    <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                        <span style={{ ...numStyle(40), lineHeight: 1 }}>{t.tracking_since ? fmt(dauShown) : "-"}</span>
                        <span style={{ fontSize: 12, color: C.muted }}>{dauHint}</span>
                    </div>
                    {dau.length ? (
                        <ResponsiveContainer width="100%" height={170}>
                            <AreaChart data={dau} margin={{ top: 6, right: 4, bottom: 0, left: -8 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis {...axisX} dataKey="date" tickFormatter={shortDate} minTickGap={36} />
                                <YAxis {...axisY} width={28} allowDecimals={false} />
                                <Tooltip {...tipLine} labelFormatter={shortDate} />
                                <Area name="DAU" type="monotone" dataKey="n" stroke={C.sageD} strokeWidth={2} fill="rgba(144,155,123,.16)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : <Empty>Daily activity is recorded from the day tracking started.</Empty>}
                </Card>
            </Grid>

            <Card title="Workout completions by week"
                right={<Legend items={[...legendTypes, { label: "Watch-pushed", color: C.watch }]} />}>
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={weeklyStacked} margin={{ top: 6, right: 4, bottom: 0, left: -6 }} barCategoryGap="22%">
                        <CartesianGrid {...gridProps} />
                        <XAxis {...axisX} dataKey="week" tickFormatter={shortDate} minTickGap={24} />
                        <YAxis {...axisY} allowDecimals={false} />
                        <Tooltip content={<StackTotalTip />} cursor={tipBar.cursor} />
                        {TYPES.map(ty => (
                            <Bar key={ty} dataKey={ty} stackId="w" fill={DISC[ty]} stroke="#FFF" strokeWidth={1} />
                        ))}
                        <Bar dataKey="Watch-pushed" stackId="w" fill={C.watch} stroke="#FFF" strokeWidth={1} radius={barRadius} />
                    </BarChart>
                </ResponsiveContainer>
            </Card>

            <Grid min={300}>
                <Card title="Active trainers per week"
                    right={<Legend items={[{ label: "Using Burst", color: C.sageD, kind: "line" }, { label: "Watch only", color: C.other, kind: "dotted" }]} />}>
                    <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={weekly} margin={{ top: 6, right: 4, bottom: 0, left: -8 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="week" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={28} allowDecimals={false} />
                            <Tooltip {...tipLine} labelFormatter={(v: any) => `w/c ${shortDate(v)}`} />
                            <Line name="Watch only" type="monotone" dataKey="watch_only" stroke={C.other} strokeWidth={2} strokeDasharray="2 4" dot={false} />
                            <Line name="Using Burst" type="monotone" dataKey="burst_trainers" stroke={C.sageD} strokeWidth={2.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Cumulative completions"
                    right={cumulative.length ? <span style={{ ...numStyle(20), lineHeight: 1 }}>{fmt(cumulative[cumulative.length - 1].cumulative)}</span> : undefined}>
                    <ResponsiveContainer width="100%" height={180}>
                        <AreaChart data={cumulative} margin={{ top: 6, right: 4, bottom: 0, left: -4 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="week" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={38} allowDecimals={false} />
                            <Tooltip {...tipLine} labelFormatter={(v: any) => `w/c ${shortDate(v)}`} />
                            <Area name="Cumulative" type="monotone" dataKey="cumulative" stroke={C.sageD} strokeWidth={2} fill="rgba(144,155,123,.16)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Active trainers by month"
                    right={<Legend items={[{ label: "Using Burst", color: C.sage }, { label: "Watch only", color: C.watch }]} />}>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={monthly} margin={{ top: 18, right: 4, bottom: 0, left: -8 }} barCategoryGap="28%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="label" tickFormatter={monthTick} />
                            <YAxis {...axisY} width={28} allowDecimals={false} />
                            <Tooltip {...tipBar} labelFormatter={monthTick} />
                            <Bar name="Using Burst" dataKey="burst_trainers" stackId="t" fill={C.sage} stroke="#FFF" strokeWidth={1} />
                            <Bar name="Watch only" dataKey="watch_only" stackId="t" fill={C.watch} stroke="#FFF" strokeWidth={1} radius={barRadius}>
                                <LabelList dataKey="trainers" position="top" style={{ ...numStyle(13), fill: C.ink }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Completions by month"
                    right={<Legend items={[{ label: "In Burst", color: C.sage }, { label: "Watch-pushed", color: C.watch }]} />}>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={monthly} margin={{ top: 18, right: 4, bottom: 0, left: -6 }} barCategoryGap="28%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="label" tickFormatter={monthTick} />
                            <YAxis {...axisY} width={32} allowDecimals={false} />
                            <Tooltip {...tipBar} labelFormatter={monthTick} />
                            <Bar name="In Burst" dataKey="burst" stackId="c" fill={C.sage} stroke="#FFF" strokeWidth={1} />
                            <Bar name="Watch-pushed" dataKey="watch" stackId="c" fill={C.watch} stroke="#FFF" strokeWidth={1} radius={barRadius}>
                                <LabelList dataKey="completions" position="top" style={{ ...numStyle(13), fill: C.ink }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Registrations by week" right={<span style={{ fontSize: 12, color: C.muted }}>peak {fmt(regPeak)}</span>}>
                    <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={regsWeekly} margin={{ top: 6, right: 4, bottom: 0, left: -8 }} barCategoryGap="18%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="week" tickFormatter={shortDate} minTickGap={36} />
                            <YAxis {...axisY} width={28} allowDecimals={false} />
                            <Tooltip {...tipBar} labelFormatter={(v: any) => `w/c ${shortDate(v)}`} />
                            <Bar name="Registrations" dataKey="n" fill={C.sageD} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card title="Stickiness" right={stickLatest ? <span style={{ ...numStyle(20), lineHeight: 1 }}>{fmt(stickLatest.stickiness)}%</span> : undefined}>
                    {stickRows.length ? (
                        <ResponsiveContainer width="100%" height={180}>
                            <LineChart data={stickRows} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis {...axisX} dataKey="date" tickFormatter={shortDate} minTickGap={36} />
                                <YAxis {...axisY} width={38} tickFormatter={(v: number) => `${v}%`} domain={[0, stickTop]} ticks={stickTicks} />
                                <Tooltip {...tipLine} labelFormatter={(v: any) => shortDate(v)}
                                    formatter={(v: any, _n: any, p: any) => [`${fmt(v, 1)}% (7-day DAU / ${fmt(p.payload.mau)} MAU)`, "Stickiness"]} />
                                <Line name="Stickiness" type="monotone" dataKey="stickiness" stroke={C.sageD} strokeWidth={2.5} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : <Empty>Needs 30 days of recording.</Empty>}
                </Card>

                <Card title="Workout mix" right={<span style={{ fontSize: 12, color: C.muted }}>{fmt(mix.total)} workouts</span>} gap={12}>
                    {mix.rows.length ? mix.rows.map(m => (
                        <BarRow key={m.name} label={TYPE_LABEL[m.name] || m.name} value={fmt(m.n)}
                            pct={mix.rows[0].n ? (m.n / mix.rows[0].n) * 100 : 0} color={discColor(m.name)} labelWidth={70} />
                    )) : <Empty>No completions in this window.</Empty>}
                </Card>
            </Grid>

            <Card title="This week's programme" right={
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {([["completed", weekCounts.done, C.ink], ["missed", weekCounts.missed, C.alert], ["upcoming", weekCounts.upcoming, C.ink]] as const).map(([label, n, col]) => (
                        <div key={label} style={{ display: "flex", gap: 6, alignItems: "baseline", background: C.panel, padding: "6px 12px", borderRadius: 10 }}>
                            <span style={{ ...numStyle(18), lineHeight: 1, color: col }}>{fmt(n)}</span>
                            <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: "rgba(22,25,37,.6)" }}>{label}</span>
                        </div>
                    ))}
                </div>
            }>
                {weekRows.length ? (<>
                    <TableWrap minWidth={860}>
                        <thead>
                            <tr>
                                <th style={th()}>Scheduled</th>
                                <th style={th()}>Member</th>
                                <th style={th()}>Workout</th>
                                <th style={th()}>Type</th>
                                <th style={th("right")}>Mins</th>
                                <th style={{ ...th(), paddingLeft: 24 }}>Status</th>
                                <th style={th("right")}>Last active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shownRows.map((r: Row) => {
                                const la = lastActive(r.last_active);
                                const type = r.activity_type || "Other";
                                const status = r.state === "completed"
                                    ? { text: "Completed", bg: "rgba(144,155,123,.18)", fg: C.sageD }
                                    : r.state === "missed"
                                        ? { text: "Missed", bg: "rgba(169,71,47,.1)", fg: C.alert }
                                        : { text: "Upcoming", bg: C.panel, fg: "rgba(22,25,37,.6)" };
                                return (
                                    <tr key={r.id} className="hq-row">
                                        <td style={{ ...td(), color: C.muted, whiteSpace: "nowrap" }}>{shortDate(r.scheduled_date)}</td>
                                        <td style={{ ...td(), fontWeight: 600, whiteSpace: "nowrap" }}>{r.user_first_name} {r.user_last_name}</td>
                                        <td style={{ ...td(), maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.name || ""}>{r.name || "-"}</td>
                                        <td style={{ ...td(), whiteSpace: "nowrap" }}>
                                            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: discColor(type), marginRight: 7 }} />
                                            {TYPE_LABEL[type] || type}
                                        </td>
                                        <td style={tdNum()}>{r.duration ? fmt(r.duration) : "-"}</td>
                                        <td style={{ ...td(), paddingLeft: 24 }}>
                                            <span style={{ fontFamily: FONT, fontWeight: 600, fontSize: 11, padding: "3px 9px", borderRadius: 999, background: status.bg, color: status.fg, whiteSpace: "nowrap" }}>{status.text}</span>
                                        </td>
                                        <td style={{ ...td("right"), fontWeight: 500, color: la.color, whiteSpace: "nowrap" }}>{la.label}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TableWrap>
                    {weekRows.length > PROGRAMME_PREVIEW && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontFamily: FONT, fontSize: 12, color: C.muted }}>
                            <span>Showing {fmt(shownRows.length)} of {fmt(weekRows.length)}</span>
                            <button onClick={() => setShowAll(s => !s)} style={{
                                fontFamily: FONT, fontWeight: 700, fontSize: 13, color: C.ink, background: "#FFF",
                                border: `1.5px solid ${C.ink}`, borderRadius: 999, padding: "7px 16px", cursor: "pointer",
                            }}>{showAll ? "Show fewer" : "Show all"}</button>
                        </div>
                    )}
                </>) : <Empty>Nothing scheduled this week.</Empty>}
            </Card>

            <EngagementCard />
        </Shell>
    );
}
